import { useCallback } from 'react';
import { Button, Dropdown, TextInput } from '@carbon/react';
import { Add, Information, TrashCan } from '@carbon/react/icons';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { enumsLoadableAtom } from './atoms';
import { useScenarioForm } from './contexts';
import { blankLineItem, blankSegment } from './utils';
import { BufferedNumberInput, parseNumber } from './buffered-number-input';
import { EscalationFields } from './escalation-fields';
import { SegmentList } from './segment-list';
import { CustomCurveEditor } from './custom-curve-editor';
import type { EnumOption, LineItem, Profile } from './types';
import styles from './differential-items-table.module.scss';

/**
 * Top-level LineItem fields writable through `setField`. Typing the field path
 * (rather than `string`) catches a mistyped key at the call site instead of
 * letting it fail silently at runtime.
 */
type LineItemField =
  | 'product'
  | 'valueType'
  | 'profile'
  | 'value'
  | 'notes'
  | 'escalation'
  | 'segments'
  | 'curve'
  | 'supportingFileSignedId'
  | '_destroySupportingFile';

/** Value-type labels shown in the dropdown (unit-oriented, per the mock). */
const VALUE_TYPE_LABELS: Record<string, string> = {
  flat: '$/unit',
  percent: '% of benchmark',
};

/** Profile labels shown in the dropdown (`escalating` reads as "Compounded"). */
const PROFILE_LABELS: Record<string, string> = {
  flat: 'Flat',
  escalating: 'Compounded',
  stepped: 'Stepped',
  custom: 'Custom',
};

/** Product-specific flat-$ unit label (oil → $/bbl, gas → $/MMBtu, ngl → $/gal). */
const FLAT_UNIT_LABELS: Record<string, string> = {
  oil: '$/bbl',
  gas: '$/MMBtu',
  ngl: '$/bbl',
};

/**
 * The flat-$ unit for a product, falling back to a generic "$/unit".
 * @param {string} product - The product enum value
 * @return {string} The unit label
 */
function flatUnitFor(product: string): string {
  return FLAT_UNIT_LABELS[product] ?? '$/unit';
}

interface DifferentialItemsTableProps {
  areaIndex: number;
}

/**
 * Editable list of differential line items for an area config. One row per
 * product (oil/gas/ngl); each row carries a temporal profile (flat / escalating
 * / stepped) whose extra inputs expand below the row. The `custom` profile is
 * shown but not yet editable.
 * @param {DifferentialItemsTableProps} props - The component props
 * @return {JSX.Element} The differentials accordion
 */
export function DifferentialItemsTable({
  areaIndex,
}: DifferentialItemsTableProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].lineItems` as const;
  const enumsState = useAtomValue(enumsLoadableAtom);
  const enums = enumsState.state === 'hasData' ? enumsState.data : null;

  const addItem = useCallback(() => {
    const current = form.getFieldValue(prefix) as LineItem[];
    form.setFieldValue(prefix, [...current, blankLineItem()]);
  }, [form, prefix]);

  const removeItem = useCallback(
    (itemIndex: number) => {
      const current = form.getFieldValue(prefix) as LineItem[];
      form.setFieldValue(
        prefix,
        current.filter((_, i) => i !== itemIndex)
      );
    },
    [form, prefix]
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <div className={styles.titleRow}>
          <h5 className={styles.title}>
            Differential Entries &nbsp;
            <TooltipIcon
              className={styles.infoIcon}
              align="bottom"
              definition="Price differentials expressed as a $/unit deduction (or premium) versus a reference price, or as a % of the reference. Each entry can be Flat (constant), Compounded (rate applied every N months or years), Stepped over defined intervals, or Custom (an uploaded curve, e.g. a basis forecast CSV)."
              onClick={(e) => e.stopPropagation()}
            >
              <Information size={16} />
            </TooltipIcon>
          </h5>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={styles.tableWrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <form.Subscribe
            selector={(state) => state.values.areas[areaIndex]?.lineItems ?? []}
          >
            {(items) => {
              // Each product can be used once per area; once every product has
              // been selected there is nothing left to add, so disable the
              // "Add Differential" button.
              const totalProducts = enums?.products.length ?? 0;
              const selectedProducts = new Set(
                items.map((item) => item.product).filter(Boolean)
              );
              const allProductsSelected =
                totalProducts > 0 && selectedProducts.size >= totalProducts;

              return (
                <>
                  {items.length > 0 && (
                    <div className={styles.rows}>
                      {items.map((item, itemIndex) => (
                        <LineItemRow
                          key={item.id}
                          areaIndex={areaIndex}
                          itemIndex={itemIndex}
                          showLabels={itemIndex === 0}
                          enums={enums}
                          allItems={items}
                          onRemove={() => removeItem(itemIndex)}
                        />
                      ))}
                    </div>
                  )}

                  {items.length === 0 && (
                    <p className={styles.empty}>
                      No differentials yet. Add one below.
                    </p>
                  )}

                  <div className={styles.footer}>
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={Add}
                      onClick={addItem}
                      disabled={allProductsSelected}
                    >
                      Add Differential
                    </Button>
                  </div>
                </>
              );
            }}
          </form.Subscribe>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

// ─────────────────────────────────────
// Single line item row
// ─────────────────────────────────────

interface LineItemRowProps {
  areaIndex: number;
  itemIndex: number;
  showLabels: boolean;
  enums: {
    products: EnumOption[];
    valueTypes: EnumOption[];
    profiles: EnumOption[];
    escalationDirections: EnumOption[];
    escalationPeriods: EnumOption[];
    tailKinds: EnumOption[];
  } | null;
  allItems: LineItem[];
  onRemove: () => void;
}

/**
 * A single line item: the product/profile/value/notes row plus the
 * profile-specific editor that expands beneath it.
 * @param {LineItemRowProps} props - The row props
 * @return {JSX.Element} The line item row
 */
function LineItemRow({
  areaIndex,
  itemIndex,
  showLabels,
  enums,
  allItems,
  onRemove,
}: LineItemRowProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].lineItems[${itemIndex}]`;

  /**
   * Sets a top-level line item field. The field name is typed (LineItemField),
   * so path typos are caught at the call site; only the structural cast to
   * TanStack's deep-key/value types remains.
   * @param {LineItemField} field - The field path within the line item
   * @param {unknown} value - The value to set
   * @return {void}
   */
  const setField = (field: LineItemField, value: unknown) =>
    form.setFieldValue(
      `${prefix}.${field}` as Parameters<typeof form.setFieldValue>[0],
      value as never
    );

  const profileItems = enums?.profiles ?? [];

  const usedProducts = new Set<string>(
    allItems
      .filter((_, i) => i !== itemIndex)
      .map((it) => it.product)
      .filter((p) => p !== '')
  );
  const availableProducts = (enums?.products ?? []).filter(
    (o) => !usedProducts.has(o.value)
  );

  return (
    <form.Subscribe
      selector={(state) => state.values.areas[areaIndex]?.lineItems[itemIndex]}
    >
      {(item) => {
        if (!item) return null;

        const isScalar =
          item.profile === 'flat' || item.profile === 'escalating';
        // Flat value type shows the product's $ unit (e.g. $/bbl); percent
        // (e.g. NGL) shows a bare "%".
        const valueUnit =
          item.valueType === 'percent' ? '%' : flatUnitFor(item.product);
        const valuePlaceholder =
          item.profile === 'stepped'
            ? 'see schedule'
            : item.profile === 'custom'
              ? 'imported curve'
              : `Enter ${valueUnit}`;

        // NGL differentials are always expressed as a percent regardless of
        // profile, so the Value Type dropdown collapses to that single option.
        const isNgl = item.product === 'ngl';
        // On a Compounded (escalating) profile the percent option is shown as
        // "% of WTI"; other NGL profiles keep the plain "Percent" label.
        const isNglCompounded = isNgl && item.profile === 'escalating';
        const valueTypeItems = isNgl
          ? (enums?.valueTypes ?? []).filter((o) => o.value === 'percent')
          : (enums?.valueTypes ?? []);

        return (
          <div className={styles.itemGroup}>
            <div className={styles.row}>
              <div className={styles.cell}>
                <Dropdown
                  id={`product-${areaIndex}-${itemIndex}`}
                  label="Select..."
                  titleText={showLabels ? 'Product' : ''}
                  hideLabel={!showLabels}
                  size="sm"
                  items={availableProducts}
                  selectedItem={
                    enums?.products.find((o) => o.value === item.product) ??
                    null
                  }
                  itemToString={(i: EnumOption | null) => i?.label ?? ''}
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: EnumOption | null;
                  }) => {
                    const product = selectedItem?.value ?? '';
                    setField('product', product);
                    // NGL differentials are expressed as a percent in v1.
                    if (product === 'ngl') setField('valueType', 'percent');
                  }}
                />
              </div>

              <div className={styles.cell}>
                <Dropdown
                  id={`profile-${areaIndex}-${itemIndex}`}
                  label="Select..."
                  titleText={showLabels ? 'Profile' : ''}
                  hideLabel={!showLabels}
                  size="sm"
                  items={profileItems}
                  selectedItem={
                    profileItems.find((o) => o.value === item.profile) ?? null
                  }
                  itemToString={(i: EnumOption | null) => {
                    if (!i) return '';
                    return PROFILE_LABELS[i.value] ?? i.label;
                  }}
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: EnumOption | null;
                  }) => {
                    if (!selectedItem || selectedItem.disabled) return;
                    changeProfile(
                      setField,
                      selectedItem.value as Profile,
                      item
                    );
                  }}
                />
              </div>

              <div className={styles.cell}>
                <Dropdown
                  id={`value-type-${areaIndex}-${itemIndex}`}
                  label="Select..."
                  titleText={showLabels ? 'Value Type' : ''}
                  hideLabel={!showLabels}
                  size="sm"
                  items={valueTypeItems}
                  selectedItem={
                    valueTypeItems.find((o) => o.value === item.valueType) ??
                    null
                  }
                  itemToString={(i: EnumOption | null) => {
                    if (!i) return '';
                    if (isNglCompounded && i.value === 'percent')
                      return '% of WTI';
                    return VALUE_TYPE_LABELS[i.value] ?? i.label;
                  }}
                  onChange={({
                    selectedItem,
                  }: {
                    selectedItem: EnumOption | null;
                  }) => setField('valueType', selectedItem?.value ?? '')}
                />
              </div>

              <div className={cx(styles.cell, styles.cellValue)}>
                <BufferedNumberInput
                  key={`value-${item.id}-${item.profile}`}
                  id={`value-${areaIndex}-${itemIndex}`}
                  labelText={showLabels ? 'Value' : ''}
                  hideLabel={!showLabels}
                  initial={
                    isScalar && item.value !== '' && item.value !== null
                      ? String(item.value)
                      : ''
                  }
                  placeholder={valuePlaceholder}
                  disabled={!isScalar}
                  onCommit={(raw) => setField('value', parseNumber(raw))}
                />
              </div>

              <div className={styles.cell}>
                <TextInput
                  id={`notes-${areaIndex}-${itemIndex}`}
                  labelText={showLabels ? 'Notes' : ''}
                  hideLabel={!showLabels}
                  size="sm"
                  placeholder="Optional"
                  value={item.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setField('notes', e.target.value)
                  }
                />
              </div>

              <div className={styles.cellAction}>
                <Button
                  kind="danger--ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Remove"
                  tooltipPosition="top"
                  onClick={onRemove}
                />
              </div>
            </div>

            {item.profile === 'escalating' && (
              <EscalationEditor item={item} enums={enums} setField={setField} />
            )}

            {item.profile === 'stepped' && (
              <SteppedEditor
                areaIndex={areaIndex}
                itemIndex={itemIndex}
                item={item}
                setField={setField}
              />
            )}

            {item.profile === 'custom' && (
              <div className={cx(styles.detail, styles.detailUnderValue)}>
                <p className={styles.detailLabel}>Custom curve</p>
                <CustomCurveEditor
                  curve={item.curve}
                  supportingFile={item.supportingFile}
                  unit={
                    item.valueType === 'percent'
                      ? '%'
                      : flatUnitFor(item.product)
                  }
                  enums={{
                    escalationDirections: enums?.escalationDirections ?? [],
                    escalationPeriods: enums?.escalationPeriods ?? [],
                    tailKinds: enums?.tailKinds ?? [],
                  }}
                  onChange={(nextCurve) => setField('curve', nextCurve)}
                  onSupportingFileUploaded={(signedId) =>
                    setField('supportingFileSignedId', signedId)
                  }
                />
              </div>
            )}
          </div>
        );
      }}
    </form.Subscribe>
  );
}

/**
 * Switches a line item's profile and resets the value/config that no longer
 * applies, so a stale shape never lingers. When switching AWAY from custom,
 * also clears the curve and the freshly-uploaded signed_id, and — if the line
 * item had a server-loaded supporting file — marks it for destruction so the
 * BE cleans up the orphan on save.
 * @param {Function} setField - Field setter (field path, value)
 * @param {Profile} profile - The new profile
 * @param {LineItem} item - The current line item (to detect a server-loaded supportingFile when leaving custom)
 * @return {void}
 */
export function changeProfile(
  setField: (field: LineItemField, value: unknown) => void,
  profile: Profile,
  item: LineItem
): void {
  // Custom-profile teardown: drop the curve and any in-flight signed_id; if the
  // server had attached a supporting file to this line item, flag it for destroy.
  if (item.profile === 'custom' && profile !== 'custom') {
    setField('curve', undefined);
    setField('supportingFileSignedId', undefined);
    if (item.supportingFile) setField('_destroySupportingFile', true);
  }

  // Switching back into custom on a row that previously had a server-loaded
  // supportingFile must unset the destroy flag — otherwise Save would still
  // emit `_destroy: true` and delete the file the user just chose to keep.
  if (profile === 'custom' && item._destroySupportingFile) {
    setField('_destroySupportingFile', false);
  }

  setField('profile', profile);
  if (profile === 'stepped') {
    setField('value', '');
    // Initialise the stepped editor only when the row has no segments yet, so a
    // previously configured schedule survives a stepped → flat → stepped round
    // trip. 'escalating' keeps the base value + default escalation config; 'flat'
    // ignores segments entirely (buildLineItemConfig returns {} for flat), so
    // neither needs to touch segments.
    if (!item.segments?.length) setField('segments', [blankSegment()]);
  }
}

// ─────────────────────────────────────
// Escalating editor (top-level Compounded profile)
// ─────────────────────────────────────

interface EscalationEditorProps {
  item: LineItem;
  enums: LineItemRowProps['enums'];
  setField: (field: LineItemField, value: unknown) => void;
}

/**
 * Thin wrapper around `<EscalationFields>` for the top-level Compounded profile.
 * Reads the line item's escalation, delegates rendering and edits to the shared
 * sub-component, and writes patches back to the form via setField.
 * @param {EscalationEditorProps} props - The editor props
 * @return {JSX.Element} The escalation editor
 */
function EscalationEditor({
  item,
  enums,
  setField,
}: EscalationEditorProps): JSX.Element {
  return (
    <div className={cx(styles.detail, styles.detailUnderValue)}>
      <p className={styles.detailLabel}>Periodic adjustment</p>
      <EscalationFields
        escalation={item.escalation}
        enums={{
          escalationDirections: enums?.escalationDirections ?? [],
          escalationPeriods: enums?.escalationPeriods ?? [],
        }}
        anchorText="Base value is the starting amount."
        idPrefix={`esc-${item.id}`}
        onChange={(patch) =>
          setField('escalation', { ...item.escalation, ...patch })
        }
      />
    </div>
  );
}

// ─────────────────────────────────────
// Stepped editor (top-level Stepped profile)
// ─────────────────────────────────────

interface SteppedEditorProps {
  areaIndex: number;
  itemIndex: number;
  item: LineItem;
  setField: (field: LineItemField, value: unknown) => void;
}

/**
 * Thin wrapper around `<SegmentList>` for the top-level Stepped profile.
 * @param {SteppedEditorProps} props - The editor props
 * @return {JSX.Element} The stepped editor
 */
function SteppedEditor({ item, setField }: SteppedEditorProps): JSX.Element {
  const unit = item.valueType === 'percent' ? '%' : flatUnitFor(item.product);
  return (
    <div className={cx(styles.detail, styles.detailUnderValue)}>
      <p className={styles.detailLabel}>
        Stepped schedule
        <span className={styles.detailHintInline}>
          The final segment extends indefinitely.
        </span>
      </p>
      <SegmentList
        segments={item.segments}
        unit={unit}
        idPrefix={`seg-${item.id}`}
        onChange={(next) => setField('segments', next)}
      />
    </div>
  );
}
